import {
  booleanAttribute,
  Component,
  computed,
  DestroyRef,
  effect,
  forwardRef,
  inject,
  input,
  linkedSignal,
  type OnInit,
  output,
  signal,
} from '@angular/core';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import type { AbstractControl } from '@angular/forms';
import {
  type AsyncValidatorFn,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  type ValidatorFn,
} from '@angular/forms';
import { type MatSelect, MatSelectModule } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { MatFormField } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslocoDirective } from '@jsverse/transloco';
import { MatSelectInfiniteScrollDirective } from './infinite-scroll/mat-select-infinite-scroll.directive';
import { MatIcon } from '@angular/material/icon';
import { isNil, isObject } from '@pct-ts/typeguard';
import {
  BehaviorSubject,
  distinctUntilChanged,
  map,
  of,
  startWith,
  Subject,
  switchMap,
} from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { debounceTime, filter, take, tap } from 'rxjs/operators';
import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay';
import { MatChip, MatChipRemove, MatChipSet } from '@angular/material/chips';
import { EnsureItemsArrayPipe } from './pipe/ensure-items-array.pipe';
import { ControlErrorComponent } from '../../error/control-error.component';
import { MarkSearchTextPipe } from './pipe/mark-search-text.pipe';
import { I18nProviderInjectionToken } from '../../i18n/scope';
import { provideI18nScope } from '@pct-ng/transloco';
import { RemoveSelectedItemsPipe } from './pipe/remove-selected-items.pipe';
import { MatIconButton } from '@angular/material/button';
import { ResolveItemLabelPipe } from './pipe/resolve-item-label.pipe';
import { NgClass } from '@angular/common';
import {
  ResolveTranslateAbleTextPipe,
  type TranslateAbleResolver,
  type TranslateAbleText,
} from '@pct-ng/i18n';
import { IsDisabledClearButtonPipe } from './pipe/is-disabled-clear-button.pipe';
import { SanitizeHtmlPipe } from '@pct-ng/common/pipe';
import type { AbstractDtoService } from '@pct-ng/core/dto';
import { ABSTRACT_DTO_SERVICE } from '@pct-ng/core/dto';
import { CustomMatSvgIconDirective } from '@pct-ng/icon';
import { assertIsNotNil } from '@pct-ts/assert';
import { handleHttpError } from '@pct-ng/core/error-http';
import type { BasePaginationDTO } from '@pct-sdk/core';
import { AbstractDelegatedFormControlComponent } from '../../reactive-forms/control/abstract-delegated-form-control.component';
import { ensureItemsArray } from '@pct-ts/common';

const i18nPrefix = `${I18nProviderInjectionToken.scope}.FIELD.AUTOCOMPLETE.`;

@Component({
  selector: 'pct-form-autocomplete',
  imports: [
    MatFormField,
    MatOption,
    NgxMatSelectSearchModule,
    MatFormField,
    MatFormFieldModule,
    MatSelectModule,
    NgxMatSelectSearchModule,
    TranslocoDirective,
    MatSelectInfiniteScrollDirective,
    ReactiveFormsModule,
    MatIcon,
    CdkConnectedOverlay,
    CdkOverlayOrigin,
    MatChipSet,
    MatChip,
    MatChipRemove,
    EnsureItemsArrayPipe,
    ControlErrorComponent,
    MarkSearchTextPipe,
    RemoveSelectedItemsPipe,
    MatIconButton,
    ResolveItemLabelPipe,
    NgClass,
    IsDisabledClearButtonPipe,
    SanitizeHtmlPipe,
    CustomMatSvgIconDirective,
    ResolveTranslateAbleTextPipe,
  ],
  templateUrl: './autocomplete.component.html',
  // changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    provideI18nScope(I18nProviderInjectionToken),
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AutocompleteComponent),
      multi: true,
    },
  ],
  host: {
    class: 'block relative',
    '[class.md:w-full]': 'fullWidth()',
    '[class.max-md:w-full]': 'fullWidthOnMobile()',
  },
})
export class AutocompleteComponent<
  SERVICE,
  CREATE_UPDATE_DTO,
  GET_DTO,
  LIST_DTO,
  ID_KEY extends keyof GET_DTO,
  GET_ALL_PARAMETERS = any,
  PAGINATED_DTO extends BasePaginationDTO = any,
  ID_TYPE = GET_DTO[ID_KEY],
>
  extends AbstractDelegatedFormControlComponent<GET_DTO | GET_DTO[] | null>
  implements OnInit
{
  readonly #destroyRef = inject(DestroyRef);
  /**
   * don't call it directly, use service() instead
   */
  readonly #diService: AbstractDtoService<
    SERVICE,
    CREATE_UPDATE_DTO,
    GET_DTO,
    LIST_DTO,
    ID_KEY,
    GET_ALL_PARAMETERS,
    PAGINATED_DTO,
    ID_TYPE
  > | null = inject(ABSTRACT_DTO_SERVICE, { optional: true });
  readonly #list$ = new BehaviorSubject<GET_DTO[]>([]);
  readonly #reset$ = new Subject<void>();
  readonly #showMultiplePanel = new BehaviorSubject(false);
  readonly #isOpenMultipleSelectedPanel = toSignal(
    this.#showMultiplePanel.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      filter(() => isNil(this.delegatedControl) || this.delegatedControl.enabled),
    ),
    { initialValue: this.#showMultiplePanel.getValue() },
  );

  readonly doScroll$ = new Subject<void>();
  readonly i18nScope = inject(I18nProviderInjectionToken);

  readonly fullWidth = input(false, { transform: booleanAttribute });
  readonly fullWidthOnMobile = input(true, { transform: booleanAttribute });
  readonly blurSelect = output<void>();
  /**
   * don't call it directly, use service() instead
   */
  readonly inputService =
    input<
      AbstractDtoService<
        SERVICE,
        CREATE_UPDATE_DTO,
        GET_DTO,
        LIST_DTO,
        ID_KEY,
        GET_ALL_PARAMETERS,
        PAGINATED_DTO,
        ID_TYPE
      >
    >();
  readonly label = input.required<TranslateAbleText>();
  readonly itemLabelResolver = input<TranslateAbleResolver<GET_DTO> | undefined | null>(undefined);
  readonly searchPlaceholder = input(`${i18nPrefix}SEARCH_PLACEHOLDER`);
  readonly extraParameters = input<Partial<GET_ALL_PARAMETERS>>({});
  /**
   * Inject translate params: searchText
   */
  readonly notFoundText = input(`${i18nPrefix}NOT_FOUND_TEXT`);
  protected sortDirection = input<'asc' | 'desc'>('asc');
  readonly pageSize = input(10);
  protected readonly equalityCheckerFn = input<MatSelect['compareWith']>(
    this.#defaultEqualityChecker.bind(this),
  );
  readonly multiple = input(false, { transform: booleanAttribute });
  readonly overrideErrors = input<Record<string, string> | undefined>(undefined);
  /**
   * Validators for the search input field.
   * @example [Validators.minLength(3), Validators.pattern(/^[a-z]+$/)]
   */
  readonly searchControlValidators = input<ValidatorFn | ValidatorFn[] | null>(null);
  /**
   * Async validators for the search input field.
   * @example [myAsyncValidator]
   */
  readonly searchControlAsyncValidators = input<AsyncValidatorFn | AsyncValidatorFn[] | null>(null);
  /**
   * Override error messages for the search input field.
   */
  readonly searchControlOverrideErrors = input<Record<string, string> | undefined>(undefined);

  protected readonly service = computed(() => {
    const inputService = this.inputService();
    if (inputService !== undefined) {
      return inputService;
    }
    assertIsNotNil(
      this.#diService,
      'Dependency injection service must be provided for autocomplete component',
    );
    return this.#diService;
  });
  protected page = 1;
  protected readonly hasNextPage = signal(true);
  protected readonly list = toSignal<GET_DTO[]>(this.#list$, {
    requireSync: true,
  });
  protected readonly loading = signal(false);
  /**
   * The linked signal is needed so that in certain places we can immediately close the panel without the timer that’s in the stream.
   */
  protected readonly isOpenMultipleSelectedPanel = linkedSignal(() =>
    this.#isOpenMultipleSelectedPanel(),
  );
  protected readonly searchControl = new FormControl<string>('', {
    nonNullable: true,
  });
  protected readonly panelClass = computed(
    () => `autocomplete-result-panel ${this.nameAttribute()}-result-panel`,
  );
  protected readonly multipleSelectedPanelClass = computed(
    () => `autocomplete-multiple-selected-panel ${this.nameAttribute()}-multiple-selected-panel`,
  );

  constructor() {
    super();
    // Apply validators to searchControl reactively when inputs change
    effect(() => {
      const validators = this.searchControlValidators();
      const asyncValidators = this.searchControlAsyncValidators();

      this.searchControl.setValidators(validators);
      this.searchControl.setAsyncValidators(asyncValidators);
      this.searchControl.updateValueAndValidity();
    });
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.#watchChanges();
  }

  protected onOpen(): void {
    /**
     * If the multiple panel is open and we click to open the select,
     * the mouse is still over the hover area, and due to the invisible overlay of the opening panel,
     * the multiple panel doesn’t receive the mouseout event and doesn’t close—so we have to handle it manually.
     */
    this.isOpenMultipleSelectedPanel.set(false);
    // elindutjuk a backend stream-et
    this.#reset$.next();
  }

  protected onClose(): void {
    /**
     * After closing the panel, the stored list must also be cleared; otherwise,
     * its contents might briefly appear the next time it is opened.
     */
    this.#reset();
  }

  protected onMouseEnter(): void {
    if (ensureItemsArray(this.delegatedControl.value).length > 0) {
      this.#showMultiplePanel.next(true);
    }
  }

  protected onMouseLeave(): void {
    this.#showMultiplePanel.next(false);
  }

  protected onRemoveSelected(item: GET_DTO): void {
    if (!this.multiple()) {
      throw new Error('Cannot remove selected item when multiple is false');
    }
    const service = this.service();
    const values = this.delegatedControl.value as GET_DTO[] | null;
    const newValues =
      values?.filter(value => service.getDtoId(value) !== service.getDtoId(item)) ?? null;
    this.delegatedControl.setValue(newValues);

    if (newValues === null || newValues.length === 0) {
      // If all items are exhausted, it must be closed immediately; otherwise, an empty panel will remain.
      this.isOpenMultipleSelectedPanel.set(false);
    }
  }

  protected trackResolveEntityId(item: GET_DTO): ID_TYPE | null | undefined {
    return this.service().getDtoId(item);
  }

  #reset(): void {
    this.hasNextPage.set(true);
    this.page = 1;
    this.#list$.next([]);
  }

  #defaultEqualityChecker(o1: GET_DTO, o2: GET_DTO): boolean {
    if (isObject(o1) || isObject(o2)) {
      const service = this.service();
      return service.getDtoId(o1) === service.getDtoId(o2);
    }
    return o1 === o2;
  }

  #watchChanges(): void {
    this.#reset$
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        // watch control change
        switchMap(() =>
          this.searchControl.valueChanges.pipe(
            startWith(this.searchControl.value),
            debounceTime(300),
            distinctUntilChanged(),
            tap(() => {
              this.#reset();
              this.loading.set(true);
            }),
          ),
        ),
        // watch scroll change
        switchMap(searchText =>
          this.doScroll$.pipe(
            startWith(searchText ?? ''),
            map(() => searchText ?? ''),
          ),
        ),
        tap(() => this.loading.set(true)),
        // Data retrieval is done through the service layer.
        switchMap(searchText => {
          if (this.searchControl.invalid) {
            return of({ data: [], pagination: { totalCount: 0, page: 1, size: this.pageSize() } });
          }

          return this.service().getAll({
            parameters: { ...this.extraParameters(), searchText } as GET_ALL_PARAMETERS,
            pagination: { page: this.page, size: this.pageSize() },
          });
        }),
        map(response => {
          if (
            !Array.isArray(response.data) ||
            // If there is no more data.
            response.data.length === 0 ||
            // If fewer items were returned than the requested page size.
            response.data.length < this.pageSize() ||
            // If we’ve reached the end and the total is exactly divisible by the page size.
            this.#list$.getValue().length + response.data.length === response.pagination.totalCount
          ) {
            this.hasNextPage.set(false);
          } else {
            this.page++;
          }

          return response.data;
        }),
        // Due to scrolling, the new portion of the list needs to be appended to the previous state.
        switchMap(response =>
          this.#list$.pipe(take(1)).pipe(map(prevList => [response ?? [], prevList])),
        ),
      )
      .subscribe({
        next: ([response, prevList]) => {
          if (this.searchControl.valid) {
            this.#list$.next(prevList.concat(response ?? []) as GET_DTO[]);
          } else {
            this.#list$.next([]);
          }
          this.loading.set(false);
        },
        error: handleHttpError(),
      });
  }

  onBlur(): void {
    this.blurSelect.emit();
  }

  clearControl(control: AbstractControl, event: Event): void {
    event.stopPropagation();
    control.setValue(null);
    control.markAsDirty();
  }
}

export default AutocompleteComponent;
